import * as React from 'react';
import {useRoute} from 'react-router5';
import styled from 'styled-components';
import {WebinarObject} from 'api/webinar/types';
import Container from 'containers/Container';
import {WebinarBuilderProps} from './types';
import {FileObject} from 'api/library';
import PreviewPicker from 'components/PreviewPicker';
import InputTitle from 'components/InputTitle';
import InputDescription from 'components/InputDescription';
import {toast} from 'react-toastify';
import validateResponse from 'helpers/validate-response';
import isError from 'helpers/isError';
import Button, {ButtonVariant} from 'components/Button';
import BuilderFooter from 'components/BuilderFooter';
import {createWebinar, editWebinar} from 'api/webinar/requests';
import Content from './components/Content';
import WebinarBuilderContext from './CourseBuilderContext';

const Wrapper = styled.div`
  position: relative;
  min-height: 100vh;
  justify-content: space-between;
  flex-direction: column;
  display: flex;
`;

const Header = styled.header`
  margin-bottom: 20px;
  column-gap: 20px;
  align-items: flex-start;
  grid-template-columns: minmax(min-content, 300px) 1fr;
  grid-auto-flow: column;
  display: grid;
`;

const PreviewContainer = styled.div`
  grid-row: span 2;
`;

const HeaderContent = styled.div`
  row-gap: 10px;
  grid-template-rows: max-content 1fr;
  display: grid;
  min-height: 170px;
`;

const Buttons = styled.div`
  gap: 12px;
  align-items: center;
  grid-auto-flow: column;
  display: inline-grid;
`;

/**
 * The component build or update webinar object.
 * @param {WebinarBuilderProps} props
 * @return {React.ReactElement<WebinarBuilderProps>}
 * @constructor
 */
export default function WebinarBuilder(
    props: WebinarBuilderProps,
): React.ReactElement<WebinarBuilderProps> {
  const {router} = useRoute();

  const [
    webinar, setWebinar,
  ] = React.useState<Partial<WebinarObject>>(
      props.defaultWebinar || {
        plugs: {
          beforePlug: {
            type: 'default',
          },
          afterPlug: {
            type: 'default',
          },
        },
    },
  );

  /**
   * Is the webinar created.
   * @return {boolean}
   */
  function isCreated(): boolean {
    return Boolean(webinar.id);
  }

  /**
   * Render the header
   * @return {JSX.Element}
   */
  function renderHeader(): JSX.Element {
    /**
     * Render preview
     * @return {JSX.Element}
     */
    function renderPreview(): JSX.Element {
      /**
       * This change event handler.
       * @param {FileObject} file
       */
      function handleChange(file: FileObject): void {
        setWebinar({
          ...webinar,
          preview: {
            id: file.id,
            type: file.type,
            url: file.url,
          },
        });
      }

      return (
        <PreviewContainer>
          <PreviewPicker
            preview={webinar.preview}
            onChange={handleChange}
          />
        </PreviewContainer>
      );
    }

    /**
     * Render title
     * @return {JSX.Element}
     */
    function renderTitle(): JSX.Element {
      /**
       * Handle title blur
       * @param {React.FocusEvent<HTMLTextAreaElement>} event
       */
      function handleBlur(
          event: React.FocusEvent<HTMLTextAreaElement>,
      ): void {
        setWebinar({
          ...webinar,
          title: event.target.value,
        });
      }

      return (
        <InputTitle
          placeholder="Введите название трансляции"
          defaultValue={webinar.title}
          onBlur={handleBlur}
        />
      );
    }

    /**
     * Render description
     * @return {JSX.Element}
     */
    function renderDescription(): JSX.Element {
      /**
       * Handle description blur
       * @param {React.FocusEvent<HTMLTextAreaElement>} event
       */
      function handleBlur(
          event: React.FocusEvent<HTMLTextAreaElement>,
      ): void {
        setWebinar({
          ...webinar,
          description: event.target.value,
        });
      }

      return (
        <InputDescription
          placeholder="Введите описание трансляции"
          defaultValue={webinar.description}
          onBlur={handleBlur}
        />
      );
    }

    return (
      <Header>
        {renderPreview()}
        <HeaderContent>
          {renderTitle()}
          {renderDescription()}
        </HeaderContent>
      </Header>
    );
  }

  /**
   * Render footer.
   * @return {JSX.Element}
   */
  function renderFooter(): JSX.Element {
    /**
     * Is the lesson data valid.
     * @return {boolean}
     */
    function isLessonValid(): boolean {
      if (!webinar.title?.length) {
        toast.error('Введите название трансляции');
        return false;
      }

      if (!webinar.description?.length) {
        toast.error('Введите описание трансляции');
        return false;
      }

      return true;
    }

    /**
     * Render create button.
     * @return {JSX.Element}
     */
    function renderCreateButton(): JSX.Element {
      /**
       * This click event handler.
       */
      function handleClick(): void {
        if (isLessonValid()) {
          createWebinar(webinar as WebinarObject).then((response) => {
            validateResponse(response);

            if (response && !isError(response)) {
              toast.success('Создан новый урок');
              router.navigate('webinars.webinar.edit', {
                id: response.id,
              });
            }
          });
        }
      }

      return (
        <Button
          type="button"
          variant={ButtonVariant.Primary}
          onClick={handleClick}
        >
          Создать
        </Button>
      );
    }

    /**
     * Render update button.
     * @return {JSX.Element}
     */
    function renderUpdateButton(): JSX.Element {
      /**
       * This click event handler.
       */
      function handleClick(): void {
        if (isLessonValid()) {
          editWebinar(webinar as WebinarObject).then((response) => {
            validateResponse(response);

            if (response && !isError(response)) {
              toast.success('Трансляция обнавлена');
            }
          });
        }
      }

      return (
        <Button
          type="button"
          variant={ButtonVariant.Primary}
          onClick={handleClick}
        >
          Сохранить
        </Button>
      );
    }

    return (
      <BuilderFooter>
        {isCreated() ? (
          <Buttons>
            {renderUpdateButton()}
          </Buttons>
        ) : (
          renderCreateButton()
        )}
      </BuilderFooter>
    );
  }

  return (
    <WebinarBuilderContext.Provider value={{
      webinar,
      setWebinar,
    }}>
      <Wrapper>
        <Container>
          {renderHeader()}
          <Content />
        </Container>
        {renderFooter()}
      </Wrapper>
    </WebinarBuilderContext.Provider>
  );
}
